import React from 'react';
import { Tool, ToolPermission, ToolStatus, ToolVersion } from '../../types/tool';

interface ToolCardProps {
  tool: Tool;
  onClick: () => void;
  isSelected: boolean;
  darkMode: boolean;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool, onClick, isSelected, darkMode }) => {
  // Get color based on permission level
  const getPermissionColor = (permission: ToolPermission) => {
    switch (permission) {
      case ToolPermission.BASIC:
        return darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800';
      case ToolPermission.ELEVATED:
        return darkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800';
      case ToolPermission.ADMIN:
        return darkMode ? 'bg-purple-800 text-purple-200' : 'bg-purple-100 text-purple-800';
      case ToolPermission.SYSTEM:
        return darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';
    }
  };

  // Get color based on category
  const getCategoryColor = (category: string) => {
    const colors = {
      communication: darkMode ? 'bg-indigo-800 text-indigo-200' : 'bg-indigo-100 text-indigo-800',
      data_management: darkMode ? 'bg-cyan-800 text-cyan-200' : 'bg-cyan-100 text-cyan-800',
      system: darkMode ? 'bg-amber-800 text-amber-200' : 'bg-amber-100 text-amber-800',
      collaboration: darkMode ? 'bg-pink-800 text-pink-200' : 'bg-pink-100 text-pink-800',
      knowledge: darkMode ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-800',
      development: darkMode ? 'bg-orange-800 text-orange-200' : 'bg-orange-100 text-orange-800',
      session: darkMode ? 'bg-violet-800 text-violet-200' : 'bg-violet-100 text-violet-800',
      integration: darkMode ? 'bg-rose-800 text-rose-200' : 'bg-rose-100 text-rose-800',
      utility: darkMode ? 'bg-teal-800 text-teal-200' : 'bg-teal-100 text-teal-800'
    };
    
    return colors[category as keyof typeof colors] || (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800');
  };

  // Get color based on status
  const getStatusColor = (status: ToolStatus) => {
    switch (status) {
      case ToolStatus.STABLE:
        return darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800';
      case ToolStatus.BETA:
        return darkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800';
      case ToolStatus.DEVELOPMENT:
        return darkMode ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case ToolStatus.EXPERIMENTAL:
        return darkMode ? 'bg-purple-800 text-purple-200' : 'bg-purple-100 text-purple-800';
      case ToolStatus.DEPRECATED:
        return darkMode ? 'bg-orange-800 text-orange-200' : 'bg-orange-100 text-orange-800';
      case ToolStatus.OFFLINE:
        return darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';
    }
  };

  // Format version string
  const formatVersion = (version?: ToolVersion) => {
    if (!version) return '';
    return `v${version.major}.${version.minor}.${version.patch}`;
  };

  return (
    <div 
      className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${
        isSelected 
          ? (darkMode ? 'bg-gray-700 border-2 border-blue-500' : 'bg-white border-2 border-blue-500 shadow-md') 
          : (darkMode ? 'bg-gray-800 border border-gray-700 hover:bg-gray-700' : 'bg-white border border-gray-200 hover:bg-gray-50 hover:shadow')
      }`}
      onClick={onClick}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className={`font-medium text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{tool.name}</h3>
        <span className={`px-2 py-1 rounded text-xs ${getPermissionColor(tool.metadata.permission_level)}`}>
          {tool.metadata.permission_level}
        </span>
      </div>
      
      <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {tool.metadata.documentation.summary}
      </p>
      
      <div className="flex flex-wrap gap-2 items-center mb-2">
        <span className={`px-2 py-1 rounded text-xs ${getCategoryColor(tool.metadata.category)}`}>
          {tool.metadata.category}
        </span>
        
        {tool.metadata.is_stateful && (
          <span className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
            stateful
          </span>
        )}
        
        {!tool.metadata.use_cache && (
          <span className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
            no cache
          </span>
        )}
      </div>

      {/* Status and version row */}
      <div className="flex justify-between items-center mt-2">
        {tool.metadata.status ? (
          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(tool.metadata.status)}`}>
            {tool.metadata.status}
          </span>
        ) : (
          <div className="group relative">
            <span className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800'}`}>
              status unknown
            </span>
            <div className="absolute left-0 bottom-full mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
              Status information not available for this tool.
            </div>
          </div>
        )}
        
        {tool.metadata.version ? (
          <span className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
            {formatVersion(tool.metadata.version)}
          </span>
        ) : (
          <div className="group relative">
            <span className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
              no version
            </span>
            <div className="absolute right-0 bottom-full mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
              Version information not available for this tool.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolCard;
